import { useLanguage } from '../context/LanguageContext';
import { X, Eye, Type, MousePointer, Monitor } from 'lucide-react';

const AccessibilityModal = ({ isOpen, onClose }) => {
  const { language } = useLanguage();

  if (!isOpen) return null;

  const content = language === 'he' ? {
    title: 'הצהרת נגישות',
    intro: 'אתר זה מחויב לספק חוויית גלישה נגישה לכלל המשתמשים, כולל אנשים עם מוגבלויות, בהתאם לתקן הנגישות הישראלי (תקנות שוויון זכויות לאנשים עם מוגבלות) ולהנחיות WCAG 2.1 ברמת AA.',
    features_title: 'התאמות נגישות שבוצעו באתר',
    features: [
      { icon: Type, text: 'טקסטים ברורים וקריאים עם היררכיית כותרות מסודרת' },
      { icon: Eye, text: 'ניגודיות צבעים מספקת בין טקסט לרקע' },
      { icon: MousePointer, text: 'כל האלמנטים האינטראקטיביים נגישים באמצעות מקלדת' },
      { icon: Monitor, text: 'האתר מותאם לתצוגה במכשירים שונים (רספונסיבי)' },
    ],
    additional: [
      'תמיכה בקוראי מסך באמצעות תגיות ARIA',
      'שפה מוגדרת בקוד האתר (עברית/אנגלית)',
      'טפסים עם תוויות ברורות והודעות שגיאה מפורשות',
      'תמונות עם תיאורים חלופיים (alt text)',
      'מבנה סמנטי תקין של העמוד',
    ],
    contact_title: 'נתקלתם בבעיית נגישות?',
    contact_text: 'אנו עושים מאמצים מתמידים לשפר את נגישות האתר. אם נתקלתם בבעיה או שיש לכם הצעות לשיפור, אנא צרו קשר:',
    email: 'eran.mazor86@gmail.com',
    updated: 'הצהרה זו עודכנה לאחרונה: אפריל 2026',
  } : {
    title: 'Accessibility Statement',
    intro: 'This website is committed to providing an accessible browsing experience for all users, including people with disabilities, in accordance with WCAG 2.1 Level AA guidelines.',
    features_title: 'Accessibility features implemented',
    features: [
      { icon: Type, text: 'Clear, readable text with proper heading hierarchy' },
      { icon: Eye, text: 'Sufficient color contrast between text and background' },
      { icon: MousePointer, text: 'All interactive elements are keyboard accessible' },
      { icon: Monitor, text: 'Responsive design for various devices' },
    ],
    additional: [
      'Screen reader support via ARIA labels',
      'Language defined in page markup (Hebrew/English)',
      'Forms with clear labels and explicit error messages',
      'Images with alternative text descriptions',
      'Proper semantic page structure',
    ],
    contact_title: 'Found an accessibility issue?',
    contact_text: 'We continuously strive to improve accessibility. If you encounter any issue or have suggestions, please contact us:',
    email: 'eran.mazor86@gmail.com',
    updated: 'This statement was last updated: April 2026',
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
      data-testid="accessibility-modal-overlay"
    >
      <div 
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-8 md:p-10 relative"
        onClick={(e) => e.stopPropagation()}
        dir={language === 'he' ? 'rtl' : 'ltr'}
        data-testid="accessibility-modal"
      >
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          data-testid="accessibility-close"
        >
          <X size={20} />
        </button>

        <h2 
          className="text-2xl md:text-3xl mb-6"
          style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--charcoal)' }}
        >
          {content.title}
        </h2>

        <p className="text-gray-600 mb-6 leading-relaxed">{content.intro}</p>

        <h3 className="font-medium text-lg mb-4" style={{ color: 'var(--charcoal)' }}>
          {content.features_title}
        </h3>

        <div className="space-y-3 mb-6">
          {content.features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div key={i} className="flex items-start gap-3">
                <Icon size={18} className="mt-1 flex-shrink-0" style={{ color: 'var(--sage-600)' }} />
                <p className="text-gray-600">{feature.text}</p>
              </div>
            );
          })}
        </div>

        <ul className="space-y-2 mb-8">
          {content.additional.map((item, i) => (
            <li key={i} className="text-gray-600 flex items-start gap-2">
              <span style={{ color: 'var(--sage-600)' }}>•</span>
              {item}
            </li>
          ))}
        </ul>

        <div 
          className="p-5 rounded-2xl mb-4"
          style={{ backgroundColor: 'var(--sage-50)', border: '1px solid var(--sage-200)' }}
        >
          <h4 className="font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
            {content.contact_title}
          </h4>
          <p className="text-gray-600 text-sm mb-2">{content.contact_text}</p>
          <a 
            href={`mailto:${content.email}`} 
            className="text-sm font-medium"
            style={{ color: 'var(--sage-600)' }}
          >
            {content.email}
          </a>
        </div>

        <p className="text-gray-400 text-xs">{content.updated}</p>
      </div>
    </div>
  );
};

export default AccessibilityModal;
